import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { trackServiceInteraction, trackButtonClick } from '../../../hooks/useGoogleAnalytics';

const ServicesGrid = () => {
  const services = [
    {
      id: 1,
      category: "Residential",
      title: "House Cleaning",
      description: "Transform your home with our professional residential cleaning services",
      icon: "Home",
      features: ["Deep cleaning", "Regular maintenance", "Eco-friendly products"],
      popular: true,
      gradient: "from-blue-500 to-purple-600",
      bgPattern: "bg-gradient-to-br from-blue-50 to-purple-50",
      serviceType: "house-cleaning"
    },
    {
      id: 2,
      category: "Commercial",
      title: "Office Cleaning",
      description: "Keep your workspace pristine with tailored commercial solutions",
      icon: "Building",
      features: ["Daily cleaning", "Sanitization", "Flexible scheduling"],
      gradient: "from-green-500 to-teal-600",
      bgPattern: "bg-gradient-to-br from-green-50 to-teal-50",
      serviceType: "office-cleaning"
    },
    {
      id: 3,
      category: "Specialized",
      title: "Carpet Cleaning",
      description: "Advanced carpet and upholstery cleaning with cutting-edge equipment",
      icon: "Layers",
      features: ["Steam cleaning", "Stain removal", "Pet odor treatment"],
      gradient: "from-orange-500 to-red-600",
      bgPattern: "bg-gradient-to-br from-orange-50 to-red-50",
      serviceType: "carpet-cleaning"
    }
  ];

  const additionalServices = [
    {
      id: 4,
      title: "Deep Cleaning",
      description: "Complete sanitization for move-ins and special occasions",
      icon: "Sparkles",
      category: "Premium",
      serviceType: "deep-cleaning"
    },
    {
      id: 5,
      title: "Vinyl Flooring",
      description: "Professional installation and maintenance",
      icon: "Square",
      category: "Flooring",
      serviceType: "flooring"
    },
    {
      id: 6,
      title: "Wooden Flooring",
      description: "Hardwood installation, refinishing & restoration",
      icon: "TreePine",
      category: "Flooring",
      serviceType: "flooring"
    }
  ];

  const handleServiceClick = (service) => {
    trackServiceInteraction(service?.title || service?.name || 'unknown_service', 'select');
    trackButtonClick(`service_${service?.id || 'unknown'}`, 'homepage_services');
  };

  const handleBookNow = (service, e) => {
    e?.stopPropagation();
    trackServiceInteraction(service?.title || service?.name || 'unknown_service', 'book');
    trackButtonClick('book_now', 'homepage_services');
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background Decoration - Contained properly */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl" style={{ willChange: 'auto' }}></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500 rounded-full blur-3xl" style={{ willChange: 'auto' }}></div>
      </div>

      <div className="container mx-auto px-4 lg:px-6 relative max-w-7xl">
        {/* Section Header - Improved alignment */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full mb-6">
            <Icon name="Star" size={20} className="text-blue-600 mr-2" />
            <span className="text-blue-800 font-semibold text-sm uppercase tracking-wide">
              Our Professional Services
            </span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-8 leading-tight">
            Exceptional Cleaning &
            <br className="hidden md:block" />
            <span className="text-blue-600"> Flooring Solutions</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            From residential cleaning to commercial flooring, we deliver comprehensive solutions 
            tailored to your specific needs with <span className="font-semibold text-blue-600">guaranteed satisfaction</span>
          </p>
        </div>

        {/* Featured Services - Hero Cards with improved grid alignment */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16 items-start">
          {services?.map((service) => (
            <div
              key={service?.id}
              onClick={() => handleServiceClick(service)}
              className={`relative ${service?.bgPattern} rounded-3xl p-8 cursor-pointer group overflow-hidden transition-all duration-500 shadow-xl hover:shadow-2xl border border-white/50 h-full flex flex-col`}
              style={{ 
                transformOrigin: 'center center',
                willChange: 'transform, box-shadow'
              }}
            >
              {/* Background Pattern - Better contained */}
              <div className="absolute top-0 right-0 w-32 h-32 opacity-10 overflow-hidden">
                <div 
                  className={`w-full h-full bg-gradient-to-br ${service?.gradient} rounded-full transition-transform duration-700`}
                  style={{ 
                    transform: 'translate(4rem, -4rem)',
                    willChange: 'transform'
                  }}
                ></div>
              </div>

              {/* Category Badge */}
              <div className="relative z-10 mb-6">
                <div className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${service?.gradient} text-white text-xs font-bold rounded-xl shadow-lg border border-white/20 backdrop-blur-sm uppercase tracking-wider`}>
                  <div className="w-2 h-2 bg-white/60 rounded-full mr-2 animate-pulse"></div>
                  {service?.category}
                </div>
              </div>

              {/* Icon */}
              <div className={`w-20 h-20 bg-gradient-to-r ${service?.gradient} rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 shadow-lg flex-shrink-0`}>
                <Icon name={service?.icon} size={32} className="text-white" />
              </div>

              {/* Content - Flex grow to fill space evenly */}
              <div className="flex-grow flex flex-col">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                  {service?.title}
                </h3>
                
                <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                  {service?.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-grow">
                  {service?.features?.map((feature, index) => (
                    <li key={index} className="flex items-center text-gray-700">
                      <div className={`w-6 h-6 bg-gradient-to-r ${service?.gradient} rounded-full flex items-center justify-center mr-3 flex-shrink-0`}>
                        <Icon name="Check" size={14} className="text-white" />
                      </div>
                      <span className="font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button - Always at bottom */}
                <Link 
                  to={`/service-details/${service?.serviceType}`}
                  onClick={(e) => handleBookNow(service, e)}
                  className={`block w-full bg-gradient-to-r ${service?.gradient} text-white py-4 px-6 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 text-center mt-auto`}
                >
                  Book Now
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Services Grid - Improved alignment */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Complete Range of Services
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            {additionalServices?.map((service) => (
              <div
                key={service?.id}
                onClick={() => handleServiceClick(service)}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl border border-gray-100 cursor-pointer group transition-all duration-300 h-full flex flex-col"
                style={{ willChange: 'transform, box-shadow' }}
              >
                {/* Category */}
                <div className="text-xs font-semibold text-blue-600 mb-3 uppercase tracking-wider">
                  {service?.category}
                </div>

                {/* Icon */}
                <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                  <Icon name={service?.icon} size={24} className="text-white" />
                </div>

                {/* Content - Flex grow for equal height */}
                <div className="flex-grow flex flex-col">
                  <h4 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {service?.title}
                  </h4>
                  
                  <p className="text-sm text-gray-600 mb-6 leading-relaxed flex-grow">
                    {service?.description}
                  </p>

                  {/* Mini CTA - Always at bottom */}
                  <Link
                    to={`/service-details/${service?.serviceType}`}
                    onClick={(e) => handleBookNow(service, e)}
                    className="block w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-all duration-300 text-sm text-center mt-auto"
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA Section - Better contained */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-3xl p-12 text-center text-white shadow-2xl relative overflow-hidden">
          {/* Background Pattern - Properly contained */}
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full" style={{ transform: 'translate(-5rem, -5rem)' }}></div>
            <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full" style={{ transform: 'translate(5rem, 5rem)' }}></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto">
            <Icon name="MessageCircle" size={48} className="text-white/80 mx-auto mb-6" />
            
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              Need a Custom Solution?
            </h3>
            
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              We're here to help with specialized cleaning and flooring needs. 
              Get a personalized quote tailored to your requirements.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center max-w-md mx-auto">
              <Link to="/smart-contact" className="flex-1">
                <button className="w-full bg-white text-blue-600 py-4 px-8 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                  Get Custom Quote
                </button>
              </Link>
              
              <Link to="/service-details" className="flex-1">
                <button className="w-full bg-transparent border-2 border-white text-white py-4 px-8 rounded-xl font-bold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300">
                  View All Services
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;